import { Injectable } from '@angular/core';
import {
  Configuration,
  ModulesPlayoffsEndpointsContractsPlayoffCoursesCreatePlayoffCourseRequestModel,
  ModulesPlayoffsEndpointsContractsPlayoffHolesCreatePlayoffHoleRequestModel,
  ModulesPlayoffsEndpointsContractsPlayoffPlayersCreatePlayoffPlayerRequestModel,
  ModulesPlayoffsEndpointsContractsPlayoffsCreatePlayoffRequest,
  PlayoffsService
} from '../../api-client';
import { HttpClient } from '@angular/common/http';
import { Playoff } from '../../models/playoff';
import { map, Observable, of, tap } from 'rxjs';
import { PlayoffDetail } from '../../models/playoff-detail';

@Injectable({
  providedIn: 'root',
})
export class PlayoffService extends PlayoffsService {
  playoffs: Playoff[] = [];

  constructor(httpClient: HttpClient, config: Configuration) {
    super(httpClient, config.basePath!, config);
    this.getAll().subscribe();
  }

  getAll(): Observable<Playoff[]> {
    return super.getPlayoffsEndpoint().pipe(
      map((playoffs: Playoff[]) =>
        playoffs.map((playoff, index) => ({
          ...playoff,
          id: index,
        }))
      ),
      tap((playoffs: Playoff[]) => {
        this.playoffs = playoffs;
      })
    );
  }

  getPlayoffByTournamentLpgaId(tournamentLpgaId: number): Observable<Playoff | undefined> {
    if (this.playoffs.length > 0) {
      return of(this.playoffs.find((playoff) => playoff.tournamentLpgaId === tournamentLpgaId));
    }

    return this.getAll().pipe(
      map((playoffs) => playoffs.find((playoff) => playoff.tournamentLpgaId === tournamentLpgaId))
    );
  }

  getPlayoffDetail(playoffId: string): Observable<PlayoffDetail> {
    const url = `${this.basePath}/playoffs/${playoffId}/detail`;

    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.get<PlayoffDetail>(url, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  createPlayoff(request: ModulesPlayoffsEndpointsContractsPlayoffsCreatePlayoffRequest): Observable<string> {
    return super.playoffsPost(request).pipe(
      tap(() => {
        this.getAll().subscribe();
      })
    );
  }

  createPlayoffCourse(playoffId: string, request: ModulesPlayoffsEndpointsContractsPlayoffCoursesCreatePlayoffCourseRequestModel): Observable<string> {
    return super.playoffsPlayoffIdCoursesPost(playoffId, request);
  }

  createPlayoffHole(
    playoffId: string,
    playoffCourseId: string,
    request: ModulesPlayoffsEndpointsContractsPlayoffHolesCreatePlayoffHoleRequestModel
  ): Observable<string> {
    return super.playoffsPlayoffIdCoursesPlayoffCourseIdHolesPost(playoffId, playoffCourseId, request);
  }

  createPlayoffPlayer(
    playoffId: string,
    request: ModulesPlayoffsEndpointsContractsPlayoffPlayersCreatePlayoffPlayerRequestModel
  ): Observable<string> {
    return super.playoffsPlayoffIdPlayersPost(playoffId, request);
  }

  updatePlayoffPlayer(
    playoffId: string,
    playoffPlayerId: string,
    request: any
  ): Observable<void> {
    const url = `${this.basePath}/playoffs/${playoffId}/players/${playoffPlayerId}`;

    let headers = this.defaultHeaders;
    headers = headers.set('Content-Type', 'application/json');
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.put<void>(url, request, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  createPlayoffScore(
    playoffId: string,
    playoffPlayerId: string,
    playoffScorecardId: string,
    request: any
  ): Observable<string> {
    const url = `${this.basePath}/playoffs/${playoffId}/players/${playoffPlayerId}/scorecards/${playoffScorecardId}/scores`;

    let headers = this.defaultHeaders;
    headers = headers.set('Content-Type', 'application/json');
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.post<string>(url, request, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  updatePlayoffScore(
    playoffId: string,
    playoffPlayerId: string,
    playoffScorecardId: string,
    playoffScoreId: string,
    request: any
  ): Observable<void> {
    const url = `${this.basePath}/playoffs/${playoffId}/players/${playoffPlayerId}/scorecards/${playoffScorecardId}/scores/${playoffScoreId}`;

    let headers = this.defaultHeaders;
    headers = headers.set('Content-Type', 'application/json');
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.put<void>(url, request, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  removePlayoffHole(
    playoffId: string,
    playoffCourseId: string,
    playoffHoleId: string
  ): Observable<void> {
    const url = `${this.basePath}/playoffs/${playoffId}/courses/${playoffCourseId}/holes/${playoffHoleId}`;

    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.delete<void>(url, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }
}
